/** Panel header (spec section 2.2 rows 1 to 5): drag handle, title, corner picker, minimize and close. */
import type { HTMLAttributes } from 'react';
import { ArrowDownLeft, ArrowDownRight, ArrowUpLeft, ArrowUpRight, Minus, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { CORNERS, CORNER_TEST_ID, type Corner } from './constants';
import { cn } from '@/lib/utils';

const CORNER_ICON = {
  'top-left': ArrowUpLeft,
  'top-right': ArrowUpRight,
  'bottom-left': ArrowDownLeft,
  'bottom-right': ArrowDownRight,
} as const;

const CORNER_LABEL: Record<Corner, string> = {
  'top-left': 'Dock top left',
  'top-right': 'Dock top right',
  'bottom-left': 'Dock bottom left',
  'bottom-right': 'Dock bottom right',
};

interface Props {
  corner: Corner;
  sending: boolean;
  dragging: boolean;
  dragHandlers: HTMLAttributes<HTMLDivElement>;
  onCorner: (corner: Corner) => void;
  onMinimize: () => void;
  onClose: () => void;
}

export function PanelHeader({ corner, sending, dragging, dragHandlers, onCorner, onMinimize, onClose }: Readonly<Props>) {
  return (
    <div
      data-testid="feedback-panel-header"
      {...dragHandlers}
      className={cn(
        'flex touch-none items-center gap-2 border-b border-border px-3 py-2 select-none',
        dragging ? 'cursor-grabbing' : 'cursor-move',
      )}
    >
      <h2 id="feedback-panel-title" className="mr-auto text-sm font-semibold text-foreground">
        Send feedback
      </h2>
      <ToggleGroup
        type="single"
        size="dense"
        variant="default"
        spacing={0}
        value={corner}
        aria-label="Panel position"
        onValueChange={(value) => {
          const next = CORNERS.find((c) => c === value);
          if (next) onCorner(next);
        }}
      >
        {CORNERS.map((c) => {
          const Icon = CORNER_ICON[c];
          return (
            <ToggleGroupItem
              key={c}
              value={c}
              aria-label={CORNER_LABEL[c]}
              title={CORNER_LABEL[c]}
              data-testid={`feedback-corner-${CORNER_TEST_ID[c]}`}
            >
              <Icon aria-hidden />
            </ToggleGroupItem>
          );
        })}
      </ToggleGroup>
      <Button
        type="button"
        variant="ghost"
        size="icon-xs"
        aria-label="Minimize feedback panel"
        data-testid="feedback-minimize"
        onClick={onMinimize}
      >
        <Minus aria-hidden />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="icon-xs"
        aria-label="Close feedback panel"
        data-testid="feedback-close"
        disabled={sending}
        onClick={onClose}
      >
        <X aria-hidden />
      </Button>
    </div>
  );
}
